import React from 'react';
import { ChevronDownIcon } from 'lucide-react';
interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
}
export function Toggle({
  checked,
  onChange,
  label,
  description
}: ToggleProps) {
  return <label className="flex cursor-pointer items-start justify-between gap-4 py-2" data-id="element-14" data-mp="1akqexd3nxb4n">
      <span className="min-w-0" data-id="element-15" data-mp="1ffe1v7j0pxh4">
        <span className="block text-sm font-medium text-slate-800" data-id="element-16" data-mp="1jf4y3qrbd5t1">{label}</span>
        {description && <span className="mt-0.5 block text-xs leading-snug text-slate-500" data-id="element-17" data-mp="1odg7y1hlzgb9">{description}</span>}
      </span>
      <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-slate-300'}`} data-id="element-18" data-mp="1te41attb898n">
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} data-id="element-19" data-mp="3yo1i519zzvf" />
      </button>
    </label>;
}
interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: {
    value: string;
    label: string;
  }[];
  label?: string;
  ariaLabel?: string;
  className?: string;
}
export function Select({
  value,
  onChange,
  options,
  label,
  ariaLabel,
  className = ''
}: SelectProps) {
  return <div className={className} data-id="element-20" data-mp="2x7gfbjgoafwx">
      {label && <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-21" data-mp="2sa6frazkknvd">
          {label}
        </span>}
      <div className="relative" data-id="element-22" data-mp="2ndg4r22mgigt">
        <select value={value} aria-label={ariaLabel ?? label} onChange={e => onChange(e.target.value)} className="w-full appearance-none rounded-lg border border-slate-300 bg-white py-2 pl-2.5 pr-8 text-sm text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-id="element-23" data-mp="2ja1kfz7nhfxk">
          {options.map(o => <option key={o.value} value={o.value} data-id="element-24" data-mp="1eal8lhs5l9b9">
              {o.label}
            </option>)}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-25" data-mp="zedriqc1ko07" />
      </div>
    </div>;
}
interface SegmentedProps<T extends string> {
  value: T;
  options: {
    value: T;
    label: string;
  }[];
  onChange: (value: T) => void;
  ariaLabel: string;
}
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel
}: SegmentedProps<T>) {
  return <div role="radiogroup" aria-label={ariaLabel} className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5" data-id="element-26" data-mp="goxl5mdoe1bv">
      {options.map(o => <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)} className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${value === o.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-27" data-mp="7nprxiq0nqon">
          {o.label}
        </button>)}
    </div>;
}